import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, ShoppingBag, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { UserBadges } from './UserBadges';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80';

// Sub-components for reduced complexity
const CardImage = ({ src, alt }) => (
  <div className="absolute inset-0 img-zoom">
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover img-desaturate"
      loading="lazy"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = FALLBACK_IMAGE;
      }}
    />
  </div>
);

const ShopButton = ({ onClick, lookId }) => (
  <button
    onClick={onClick}
    className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1.5 bg-[#C41E3A] text-white text-xs uppercase tracking-wider font-bold rounded-sm hover:bg-[#A01830] transition-all opacity-0 group-hover:opacity-100 shadow-lg"
    data-testid={`shop-btn-${lookId}`}
  >
    <ShoppingBag className="w-3.5 h-3.5" />
    <span>Shop</span>
    <ExternalLink className="w-3 h-3" />
  </button>
);

const BrandTags = ({ tags, lookId }) => {
  if (!tags || tags.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-2 mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
      {tags.map((tag) => (
        <span key={`${lookId}-${tag}`} className="tag-pill text-[10px]">{tag}</span>
      ))}
    </div>
  );
};

const LikeButton = ({ isLiked, likesCount, onClick, lookId }) => (
  <button
    onClick={onClick}
    className={`like-btn flex items-center gap-1.5 text-sm ${isLiked ? 'liked text-[#C41E3A]' : 'text-gray-400 hover:text-white'} transition-colors`}
    data-testid={`like-btn-${lookId}`}
  >
    <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
    <span>{likesCount}</span>
  </button>
);

const CardActions = ({ isLiked, likesCount, commentsCount, onLike, onShop, hasAffiliateUrl, lookId }) => (
  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
    <div className="flex items-center gap-4">
      <LikeButton isLiked={isLiked} likesCount={likesCount} onClick={onLike} lookId={lookId} />
      <div className="flex items-center gap-1.5 text-gray-400 text-sm">
        <MessageCircle className="w-4 h-4" />
        <span>{commentsCount}</span>
      </div>
    </div>
    
    {hasAffiliateUrl && (
      <button
        onClick={onShop}
        className="flex items-center gap-1.5 text-[#C41E3A] text-sm hover:text-white transition-colors"
        data-testid={`shop-link-${lookId}`}
      >
        <ShoppingBag className="w-4 h-4" />
        <span className="text-xs uppercase tracking-wider">Shop Look</span>
      </button>
    )}
  </div>
);

const FashionCard = ({ look, onLikeUpdate, featured = false }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isLiked, setIsLiked] = useState(look.liked_by_user);
  const [likesCount, setLikesCount] = useState(look.likes_count);
  const [isLiking, setIsLiking] = useState(false);

  const handleLike = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Please sign in to like looks');
      return;
    }

    if (isLiking) return;
    setIsLiking(true);

    try {
      const response = await axios.post(`${API}/looks/${look.id}/like`);
      setIsLiked(response.data.liked);
      setLikesCount(prev => response.data.liked ? prev + 1 : prev - 1);
      if (onLikeUpdate) onLikeUpdate(look.id, response.data.liked);
    } catch {
      toast.error('Failed to update like');
    } finally {
      setIsLiking(false);
    }
  }, [isAuthenticated, isLiking, look.id, onLikeUpdate]);

  const handleShopClick = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!look.affiliate_url) {
      toast.info('Shop link coming soon!');
      return;
    }

    // Open the link immediately (synchronous) to avoid popup blockers
    window.open(look.affiliate_url, '_blank', 'noopener,noreferrer');

    // Track the click in the background
    try {
      await axios.post(`${API}/affiliate/click/${look.id}`);
    } catch (err) {
      // Tracking failure is non-blocking — the user already got to the store
      console.error('Affiliate click tracking failed:', err.message);
    }
  }, [look.affiliate_url, look.id]);

  const cardClassName = `group relative overflow-hidden bg-[#1c1c1c] border border-white/5 hover:border-white/20 transition-all duration-500 card-shine h-[350px] sm:h-[380px] md:h-[400px] cursor-pointer ${featured ? 'bento-featured' : ''}`;

  const handleCardClick = () => {
    navigate(`/look/${look.id}`);
  };

  return (
    <div className={cardClassName} data-testid={`fashion-card-${look.id}`} onClick={handleCardClick}>
      <CardImage src={look.image_url} alt={look.title} />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />

      {look.affiliate_url && <ShopButton onClick={handleShopClick} lookId={look.id} />}

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end p-6">
        <BrandTags tags={look.brand_tags} lookId={look.id} />

        <h3 className="text-lg font-serif text-white mb-1 line-clamp-2">{look.title}</h3>
        <div className="flex items-center gap-1.5">
          <Link 
            to={`/profile/${look.username}`}
            className="text-gray-400 text-sm hover:text-[#C41E3A] transition-colors"
            data-testid={`look-user-${look.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            @{look.username}
          </Link>
          <UserBadges 
            role={look.user_role} 
            badges={look.user_badges} 
            isVerified={look.user_verified} 
            size="xs" 
            showRole={false} 
          />
        </div>

        <CardActions
          isLiked={isLiked}
          likesCount={likesCount}
          commentsCount={look.comments_count}
          onLike={handleLike}
          onShop={handleShopClick}
          hasAffiliateUrl={!!look.affiliate_url}
          lookId={look.id}
        />
      </div>
    </div>
  );
};

export default FashionCard;
